import React from 'react'
import { Avatar } from '@/components/Avatar'
import { useTranslation } from '@/localization/useTranslation'
import { useProfileCompleteness } from '@/hooks/users/useProfileCompleteness'
import { PROFILE_AVATAR_TARGET_ID } from '@/constants/profileCompleteness'
import { MENU_TRIGGER_ATTRIBUTE } from '../hooks/useMobileMenu'

import classes from '../styles.module.scss'

interface ProfileAvatarProps {
  profileName?: string
  /** Mobile has no hover, so the avatar opens the account menu itself. */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
}

/**
 * The avatar plus the ring that marks the profile as missing something. The
 * ring never explains itself — the account menu this avatar opens carries the
 * entry that does, so the gesture people already make is the one that answers.
 */
export const ProfileAvatar: React.FC<ProfileAvatarProps> = ({ profileName, onClick }) => {
  const { t } = useTranslation()
  const { shouldShowIndicator } = useProfileCompleteness()

  const avatar = (
    <Avatar
      id={PROFILE_AVATAR_TARGET_ID}
      name={profileName || ''}
      isIncomplete={shouldShowIndicator}
    />
  )

  if (!onClick) return avatar

  return (
    <button
      type="button"
      {...{ [MENU_TRIGGER_ATTRIBUTE]: true }}
      className={classes.avatarButton}
      aria-label={shouldShowIndicator ? t('Profile incomplete') : t('Profile')}
      onClick={onClick}
    >
      {avatar}
    </button>
  )
}
